import FileSaver from 'file-saver';
import _ from 'lodash';
import moment from 'moment';
import * as XLSX from 'xlsx-js-style';
import * as ActionTypes from '../actions/actionTypes';
import exportConfig from '../config/export_config.json';
import indicatorConfig from '../config/indicators.json';
import {DATA_SETS, EXPORT_FORMATS, MODES, VI_Scales} from '../Constants';
import {buildIspTableData, getIspColumnMetadata, getIspExportDateRangeKeys} from '../utils/isp_dataset';
import {
  applyLagTempRhShift,
  displayModificationsForLag,
  LAG_TEMP_RH_OPTIONS,
  lagHoursForDataSet,
  lagTempRhLabel
} from '../utils/lag_weather_utils';
import {
  DEFAULT_SUMMER_AFTERNOON_FMC,
  SUMMER_AFTERNOON_FMC_ENUM,
  SUMMER_AFTERNOON_FMC_USER_CONFIGURABLE
} from '../utils/behaviour_models/shared/ffm';
import {FFDI_COLORS, FH_COLORS} from '../reducers/point_data_mcarthur_mk5';
import {rgbToHex} from '../utils/point_utils';
import {toPrescriptionIcon} from '../utils/prescriptions_utils';
import {addDays, lastMidnightDate} from '../utils/time_utils';
import {toCardinal} from '../utils/wind_utils';

const DATE_FORMAT = 'ddd DD/MM/YYYY HH:mm';

const XLSX_THIN_BORDER = {
  top: { style: 'thin', color: { rgb: 'CCCCCC' } },
  right: { style: 'thin', color: { rgb: 'CCCCCC' } },
  bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
  left: { style: 'thin', color: { rgb: 'CCCCCC' } }
};

const INDICATOR_BY_PREFIX = _.keyBy(indicatorConfig, 'prefix');

const FORMATS_SIMPLE_INDICATORS = new Set([
  EXPORT_FORMATS.WEATHER,
  EXPORT_FORMATS.SIMPLE_PLUS_BEHAVIOUR,
  EXPORT_FORMATS.SIMPLE_PLUS_PRESCRIPTIONS
]);

const FORMATS_DETAILED_INDICATORS = new Set([
  EXPORT_FORMATS.DETAILED_WEATHER,
  EXPORT_FORMATS.DETAILED_PLUS_BEHAVIOUR,
  EXPORT_FORMATS.DETAILED_PLUS_PRESCRIPTIONS
]);

const FORMATS_WITH_SIDE_TABLE = new Set([
  EXPORT_FORMATS.DETAILED_PLUS_BEHAVIOUR,
  EXPORT_FORMATS.SIMPLE_PLUS_BEHAVIOUR,
  EXPORT_FORMATS.DETAILED_PLUS_PRESCRIPTIONS,
  EXPORT_FORMATS.SIMPLE_PLUS_PRESCRIPTIONS
]);

const XLSX_EXPORT_FORMATS = new Set([
  EXPORT_FORMATS.WEATHER,
  EXPORT_FORMATS.DETAILED_WEATHER,
  EXPORT_FORMATS.SIMPLE_PLUS_BEHAVIOUR,
  EXPORT_FORMATS.DETAILED_PLUS_BEHAVIOUR,
  EXPORT_FORMATS.SIMPLE_PLUS_PRESCRIPTIONS,
  EXPORT_FORMATS.DETAILED_PLUS_PRESCRIPTIONS,
  EXPORT_FORMATS.ISP
]);

const MK5_TABLE_HEADING = {
  FDI: { displayName: 'FDI', mainTable: true },
  FH_MK5: { displayName: 'MK5 Flame Height', behaviourSummaryTable: true },
  FROS_MK5: { displayName: 'MK5 FROS (m/h)', behaviourSummaryTable: true }
};

const L80_TABLE_HEADING = {
  FMC: { displayName: 'L80 FMC (%)', behaviourSummaryTable: true },
  FH_L80: { displayName: 'L80 Flame Height (m)', behaviourSummaryTable: true },
  SH: { displayName: 'L80 Scorch Height (m)', behaviourSummaryTable: true },
  FROS_L80: { displayName: 'L80 FROS (m/h)', behaviourSummaryTable: true }
};

const BEHAVIOUR_NOTE = {
  [DATA_SETS.MCARTHUR_MK5]:
    'The FDI values under the Weather heading are calculated by TSU. The FDI value under the McArthur Mk5 heading is calculated by Foresight. These may differ slightly due to different methods of incorporating wind within their calculation. Please refer to the Foresight User Guide.',
  [DATA_SETS.GRASS]:
    'The FDI values under the Weather heading are calculated by TSU. The FDI value under the Grass heading is calculated by Foresight. These may differ slightly due to different methods of incorporating wind within their calculation. Please refer to the Foresight User Guide.'
};

const DATA_SETS_WITH_LAG_TEMP_RH_OPTIONS = new Set([
  DATA_SETS.MCARTHUR_MK5,
  DATA_SETS.LEAFLET_80,
  DATA_SETS.VESTA,
  DATA_SETS.VESTA_MK2_DRY,
  DATA_SETS.VESTA_MK2_WET,
  DATA_SETS.MALLEE_HEATH
]);

const DATA_SETS_WITH_SUMMER_AFTERNOON_FMC = new Set([
  DATA_SETS.VESTA,
  DATA_SETS.VESTA_MK2_DRY,
  DATA_SETS.VESTA_MK2_WET
]);

const SIDE_TABLE_SLICES = {
  [DATA_SETS.MCARTHUR_MK5]: s => s.point_data_mcarthur_mk5,
  [DATA_SETS.LEAFLET_80]: s => s.point_data_leaflet_80,
  [DATA_SETS.VESTA]: s => s.point_data_vesta,
  [DATA_SETS.VESTA_MK2_DRY]: s => s.point_data_vesta_mk2_dry,
  [DATA_SETS.VESTA_MK2_WET]: s => s.point_data_vesta_mk2_wet,
  [DATA_SETS.GRASS]: s => s.point_data_grass,
  [DATA_SETS.MALLEE_HEATH]: s => s.point_data_mallee_heath,
  [DATA_SETS.PRESCRIPTIONS]: s => s.point_data_prescriptions
};

const FILENAME_BY_FORMAT = {
  [EXPORT_FORMATS.PHOENIX]: d => `ForesightExport_Phoenix_${d}.csv`,
  [EXPORT_FORMATS.WEATHER]: d => `ForesightExport_Weather_Simple_${d}.xlsx`,
  [EXPORT_FORMATS.DETAILED_WEATHER]: d => `ForesightExport_Weather_Detailed_${d}.xlsx`,
  [EXPORT_FORMATS.SIMPLE_PLUS_BEHAVIOUR]: d => `ForesightExport_Simple_Plus_Behaviour_${d}.xlsx`,
  [EXPORT_FORMATS.DETAILED_PLUS_BEHAVIOUR]: d => `ForesightExport_Detailed_Plus_Behaviour_${d}.xlsx`,
  [EXPORT_FORMATS.SIMPLE_PLUS_PRESCRIPTIONS]: d => `ForesightExport_Simple_Plus_Prescriptions_${d}.xlsx`,
  [EXPORT_FORMATS.DETAILED_PLUS_PRESCRIPTIONS]: d => `ForesightExport_Detailed_Plus_Prescriptions_${d}.xlsx`,
  [EXPORT_FORMATS.ISP]: d => `ForesightExport_ISP_${d}.xlsx`
};

function _val(orig, mods, key) {
  return mods && mods[orig.name] && typeof mods[orig.name][key] !== 'undefined' ? mods[orig.name][key] : orig[key];
}

function isModified(orig, mods, key) {
  return mods && mods[orig.name] && typeof mods[orig.name][key] !== 'undefined';
}

function getIndicatorKeysForExport(format, app_mode) {
  const planned = app_mode === MODES.PLANNED_BURNS;
  if (FORMATS_SIMPLE_INDICATORS.has(format)) {
    return planned ? exportConfig.indicatorKeys_simple_plannedBurns : exportConfig.indicatorKeys_simple_wildfire;
  }
  if (FORMATS_DETAILED_INDICATORS.has(format)) {
    return planned ? exportConfig.indicatorKeys_detailed_plannedBurns : exportConfig.indicatorKeys_detailed_wildfire;
  }
  return undefined;
}

function getSideTableSlice(state, data_set) {
  const pick = SIDE_TABLE_SLICES[data_set];
  return pick ? pick(state) : {};
}

function displayNameForIndicatorKey(k, indicatorByPrefix) {
  const meta = indicatorByPrefix[k];
  if (!meta) {
    return null;
  }
  const units = meta.units ? (meta.units === 'kts' ? ' (km/h)' : ` (${meta.units})`) : '';
  return `${meta.displayName}${units}`;
}

function appendMainTableHeading(tableHeading, k, indicatorByPrefix) {
  const name = displayNameForIndicatorKey(k, indicatorByPrefix);
  if (name) {
    tableHeading.push({ displayName: name, mainTable: true });
  }
}

function appendBehaviourSummaryHeading(tableHeading, k) {
  const mk5 = MK5_TABLE_HEADING[k];
  if (mk5) {
    tableHeading.push(mk5);
    return true;
  }
  const l80 = L80_TABLE_HEADING[k];
  if (l80) {
    tableHeading.push(l80);
    return true;
  }
  return false;
}

function buildWeatherExportHeadings(indicatorKeys, indicatorByPrefix) {
  const tableHeading = [{ displayName: 'Time', mainTable: true }];
  indicatorKeys.forEach(k => {
    if (indicatorByPrefix[k]) {
      appendMainTableHeading(tableHeading, k, indicatorByPrefix);
    } else if (exportConfig.MK5_key.includes(k) || exportConfig.Leaflet80_key.includes(k)) {
      appendBehaviourSummaryHeading(tableHeading, k);
    } else if (k === 'HP_Wind_Dir_CARDINALS') {
      tableHeading.push({ displayName: 'Wind Direction', mainTable: true });
    }
  });
  return tableHeading;
}

function mk5CellValue(k, row) {
  if (!row) {
    return undefined;
  }
  if (k === 'FH_MK5') {
    return row.FH;
  }
  if (k === 'FROS_MK5') {
    return row.FROS;
  }
  return row[k];
}

function leaflet80CellValue(k, row) {
  if (!row) {
    return undefined;
  }
  if (k === 'FH_L80') {
    return row.FH;
  }
  if (k === 'FROS_L80') {
    return row.FROS;
  }
  return row[k];
}

function weatherExportCell(k, w, mods, rowKey, mk5TableData, leafletTableData) {
  let val;
  let modified;

  if (exportConfig.MK5_key.includes(k)) {
    val = mk5CellValue(k, mk5TableData[rowKey]);
  } else if (exportConfig.Leaflet80_key.includes(k)) {
    val = leaflet80CellValue(k, leafletTableData[rowKey]);
  } else if (k === 'HP_Wind_Dir_CARDINALS') {
    val = toCardinal(_val(w, mods, 'HP_Wind_Dir_SFC'));
  } else if (k === 'VI') {
    const raw = _val(w, mods, k);
    const valid_scales = _.keys(VI_Scales.scale)
      .sort((a, b) => Number(a) - Number(b))
      .filter(m => Number(raw) < Number(m));
    val = valid_scales.length > 0 ? VI_Scales.scale[valid_scales[0]] : VI_Scales.else;
  } else {
    val = _val(w, mods, k);
    modified = isModified(w, mods, k);
  }

  const useTextType = k === 'HP_Wind_Dir_CARDINALS' || k === 'VI';
  return { val, modified, useTextType };
}

function _hpIspData(state, mods, start_time, end_time) {
  const colMeta = getIspColumnMetadata(state.point_data_weather.table_meta_data, state.point_data_grass.table_meta_data);
  const tableData = buildIspTableData(
    state.point_data_weather.table_data,
    state.point_data_mcarthur_mk5.table_data,
    state.point_data_grass.table_data
  );
  const wb = XLSX.utils.book_new();
  const xlsxLines = [];
  getSheetHeader(state, EXPORT_FORMATS.ISP, getIspExportDateRangeKeys()).forEach(row => xlsxLines.push(row));
  xlsxLines.push([]);

  const headerStyleBase = {
    alignment: { textRotation: 180, horizontal: 'center' },
    font: { bold: true },
    border: XLSX_THIN_BORDER
  };

  const headerCells = colMeta.map(m => {
    const rgb = (m.headerColor || '#9CBCD3').replace(/^#/, '').toUpperCase();
    return {
      v: m.displayName,
      t: 's',
      s: { ...headerStyleBase, fill: { fgColor: { rgb } } }
    };
  });

  xlsxLines.push([
    {
      v: 'Time',
      t: 's',
      s: {
        alignment: { horizontal: 'center' },
        font: { bold: true },
        border: XLSX_THIN_BORDER,
        fill: { fgColor: { rgb: 'CCCCCC' } }
      }
    },
    ...headerCells
  ]);

  const weather = _.sortBy(_.values(state.point_data_weather.table_data), 'sortIndex');
  weather.filter(w => w.sortIndex >= start_time && w.sortIndex < end_time).forEach((w, index) => {
    const row = [];
    row.push({ v: moment(w.time).format(DATE_FORMAT), t: 's', s: getCellStyle(false, null, '', index) });
    const dataRow = tableData[w.name];
    colMeta.forEach((m) => {
      const val = dataRow ? _val(dataRow, mods, m.key) : undefined;
      const modified = isModified(w, mods, m.key);
      const empty = val === undefined || val === null || val === '';
      const isNum = typeof val === 'number' && !isNaN(val);
      row.push({
        v: empty ? '' : val,
        t: empty ? 's' : (isNum ? 'n' : 's'),
        s: getCellStyle(modified, empty ? null : val, m.key, index)
      });
    });
    xlsxLines.push(row);
  });

  const ws = XLSX.utils.aoa_to_sheet(xlsxLines);
  const colCount = colMeta.length + 1;
  ws['!cols'] = setWidths([18].concat(Array.from({ length: colCount - 1 }, () => 12)));
  XLSX.utils.book_append_sheet(wb, ws, 'ISP');
  return wb;
}

function _hpPhoenixData(state, mods, start_time, end_time) {
  const csvLines = ['Time, Temp, RH, WindDir, WindSpd, DF, Curing, Cloud'];
  const weather = _.sortBy(_.values(state.point_data_weather.table_data), 'sortIndex');
  weather.filter(w => w.sortIndex >= start_time && w.sortIndex < end_time).forEach(w => {
    csvLines.push([
      moment(w.time).format(DATE_FORMAT),
      _val(w, mods, 'HP_T_SFC'),
      _val(w, mods, 'HP_RH_SFC'),
      _val(w, mods, 'HP_Wind_Dir_SFC'),
      _val(w, mods, 'HP_WindMag_SFC'),
      _val(w, mods, 'HP_DF_SFC'),
      _val(w, mods, 'HP_Curing_SFC'),
      _val(w, mods, 'HP_Sky_SFC')
    ].join(','));
  });
  return csvLines.join('\n');
}

function getScaleColor(cellValue, colors) {
  const valid_colors = _.keys(colors.scale)
    .sort((a, b) => Number(a) - Number(b))
    .filter(k => Number(cellValue) < Number(k));
  const color = valid_colors.length > 0 ? colors.scale[valid_colors[0]] : colors.else;
  const rgbNums = color.split(',');
  return rgbToHex(rgbNums[0].split('(')[1], rgbNums[1], rgbNums[2]);
}

function getCellStyle(isValueModified, cellValue, key, index, fgColor = '') {
  const style = {
    alignment: { horizontal: 'center' },
    border: { ...XLSX_THIN_BORDER }
  };
  if (isValueModified) {
    style.border.left = { style: 'thick', color: { rgb: '0000FF' } };
  }
  if (fgColor) {
    style.fill = { fgColor: { rgb: fgColor } };
    return style;
  }
  if (key === 'FDI') {
    style.fill = { fgColor: { rgb: getScaleColor(cellValue, FFDI_COLORS) } };
    return style;
  }
  if (key === 'FH_MK5' || key === 'FH_L80') {
    style.fill = { fgColor: { rgb: getScaleColor(cellValue, FH_COLORS) } };
    return style;
  }
  const layer = INDICATOR_BY_PREFIX[key];
  if (layer && layer.colors) {
    style.fill = { fgColor: { rgb: getScaleColor(cellValue, layer.colors) } };
    return style;
  }
  if (index % 2 === 1) {
    style.fill = isValueModified ? { fgColor: { rgb: 'E6F2FF' } } : { fgColor: { rgb: 'CCCCCC' } };
  } else {
    style.fill = isValueModified ? { fgColor: { rgb: 'E6F2FF' } } : { fgColor: { rgb: 'FFFFFF' } };
  }
  return style;
}

const tableHeaderLabelConfig = (value) => ({ v: value, t: 's', s: { font: { bold: true, color: { rgb: '000000' } } } });
const tableHeaderValueConfig = (value) => ({
  v: value,
  t: 's',
  s: { font: { bold: false, color: { rgb: '000000' } } }
});
const blankCell = () => ({});

const getDataUpdatedTimeRange = (indicators, indicatorKeys) => {
  let minDate;
  let maxDate;
  indicators.forEach((indicator) => {
    if (indicator.prefix && indicatorKeys.includes(indicator.prefix) && indicator.creationTime) {
      const t = new Date(indicator.creationTime);
      if (minDate === undefined || t < minDate) {
        minDate = t;
      }
      if (maxDate === undefined || t > maxDate) {
        maxDate = t;
      }
    }
  });
  return { minDate, maxDate };
};

function addLeaflet80Headers(state, tableHeader) {
  const { fine_fuel_load, slope, wind_reduction_factor } = state.point_data_leaflet_80;
  tableHeader.push([
    tableHeaderLabelConfig('Fine Fuel Load (t/ha)'),
    tableHeaderValueConfig(fine_fuel_load),
    tableHeaderLabelConfig('Slope (°)'),
    tableHeaderValueConfig(slope),
    tableHeaderLabelConfig('WRF (1-6)'),
    tableHeaderValueConfig(wind_reduction_factor)
  ]);
}

function addLagTempRhHeader(state, tableHeader) {
  const { data_set } = state.graphs;
  if (!DATA_SETS_WITH_LAG_TEMP_RH_OPTIONS.has(data_set)) {
    return;
  }
  const slice = getSideTableSlice(state, data_set);
  if (!slice) {
    return;
  }
  const lag = slice.lag_temp_rh === undefined ? LAG_TEMP_RH_OPTIONS.OFF : slice.lag_temp_rh;
  tableHeader.push([
    tableHeaderLabelConfig('Lag Temp & RH'),
    tableHeaderValueConfig(lagTempRhLabel(lag))
  ]);
}

function addSummerAfternoonFmcHeader(state, tableHeader) {
  // Hidden while SUMMER_AFTERNOON_FMC_USER_CONFIGURABLE is false (operators cannot change the setting).
  if (!SUMMER_AFTERNOON_FMC_USER_CONFIGURABLE) {
    return;
  }
  const { data_set } = state.graphs;
  if (!DATA_SETS_WITH_SUMMER_AFTERNOON_FMC.has(data_set)) {
    return;
  }
  const slice = getSideTableSlice(state, data_set);
  if (!slice) {
    return;
  }
  const pref = slice.summer_afternoon_fmc === undefined ?
    DEFAULT_SUMMER_AFTERNOON_FMC :
    slice.summer_afternoon_fmc;
  tableHeader.push([
    tableHeaderLabelConfig('Summer afternoon FMC'),
    tableHeaderValueConfig(_.findKey(SUMMER_AFTERNOON_FMC_ENUM, v => v === pref))
  ]);
}

const getSheetHeader = (state, format, indicatorKeys) => {
  const tableHeader = [];
  tableHeader.push([tableHeaderLabelConfig('BOM Weather Data')]);

  const active_incident = state.incidents.active;
  if (active_incident && active_incident.properties) {
    tableHeader.push([tableHeaderLabelConfig('Burn Name'), tableHeaderValueConfig(active_incident.properties.Name)]);
  }

  const { lat, lng, fuel_type } = state.point;
  tableHeader.push([
    tableHeaderLabelConfig('Lat'),
    tableHeaderValueConfig(lat.toFixed(4)),
    tableHeaderLabelConfig('Long'),
    tableHeaderValueConfig(lng.toFixed(4))
  ]);

  tableHeader.push([tableHeaderLabelConfig('Phoenix Fuel Type'), tableHeaderValueConfig(fuel_type)]);

  if (format === EXPORT_FORMATS.SIMPLE_PLUS_BEHAVIOUR || format === EXPORT_FORMATS.DETAILED_PLUS_BEHAVIOUR) {
    addLeaflet80Headers(state, tableHeader);
    addLagTempRhHeader(state, tableHeader);
    addSummerAfternoonFmcHeader(state, tableHeader);
  }

  tableHeader.push([tableHeaderLabelConfig('Date Report Produced'), tableHeaderValueConfig(moment(new Date()).format(DATE_FORMAT))]);

  const { minDate, maxDate } = getDataUpdatedTimeRange(state.indicators.indicators, indicatorKeys);
  tableHeader.push([tableHeaderLabelConfig('Date Updated')]);
  tableHeader.push([
    tableHeaderLabelConfig('Earliest'),
    tableHeaderValueConfig(moment(minDate).format(DATE_FORMAT)),
    blankCell(), blankCell(),
    tableHeaderLabelConfig('Latest'),
    tableHeaderValueConfig(moment(maxDate).format(DATE_FORMAT))
  ]);

  if (
    (format === EXPORT_FORMATS.SIMPLE_PLUS_BEHAVIOUR || format === EXPORT_FORMATS.DETAILED_PLUS_BEHAVIOUR) &&
    BEHAVIOUR_NOTE[state.graphs.data_set]
  ) {
    tableHeader.push([]);
    tableHeader.push([tableHeaderLabelConfig('NOTE'), tableHeaderValueConfig(BEHAVIOUR_NOTE[state.graphs.data_set])]);
  }

  tableHeader.push([]);
  return tableHeader;
};

const getSideTableHeader = (state, data_set) => {
  const slice = getSideTableSlice(state, data_set);
  const meta_data = slice.table_meta_data || [];
  return meta_data.filter(m => !m.nonFinal && !m.hide_in_table).map(m => ({
    displayName: m.displayName,
    key: m.key,
    colors: m.colors
  }));
};

function headingCellFillColor(cell) {
  if (cell.displayName === 'Time') {
    return '9CBCD3';
  }
  if (cell.behaviourSummaryTable) {
    return '7FB1ED';
  }
  if (cell.mainTable) {
    return '67A1EA';
  }
  return '9CBCD3';
}

function rotatedColumnHeaderCell(cell) {
  return {
    v: cell.displayName,
    t: 's',
    s: {
      alignment: { textRotation: cell.displayName === 'Time' ? 0 : 180, horizontal: 'center' },
      font: { bold: true },
      border: XLSX_THIN_BORDER,
      fill: { fgColor: { rgb: headingCellFillColor(cell) } }
    }
  };
}

function _hpWeatherData(state, mods, format, start_time, end_time) {
  const { data_set } = state.graphs;
  const { app_mode } = state.app;
  const indicatorKeys = getIndicatorKeysForExport(format, app_mode);

  const wb = XLSX.utils.book_new();
  const xlsxLines = [];

  getSheetHeader(state, format, indicatorKeys).forEach(row => xlsxLines.push(row));

  const tableHeading = buildWeatherExportHeadings(indicatorKeys, INDICATOR_BY_PREFIX);

  let sideTableHeader = [];
  let sideTableData = {};
  if (FORMATS_WITH_SIDE_TABLE.has(format)) {
    sideTableHeader = getSideTableHeader(state, data_set);
    sideTableHeader.forEach(h => {
      tableHeading.push({ displayName: h.displayName, mainTable: false });
    });
    sideTableData = getSideTableSlice(state, data_set).table_data || {};
  }

  const table_name_row = Array.from({ length: indicatorKeys.length + sideTableHeader.length }, () => '');
  table_name_row[1] = {
    v: 'Weather',
    t: 's',
    s: { fill: { fgColor: { rgb: '67A1EA' } }, alignment: { horizontal: 'center' } }
  };

  table_name_row[indicatorKeys.length - exportConfig.behaviourSummaryWidth + 1] = {
    v: 'Behaviour Summary',
    t: 's',
    s: { fill: { fgColor: { rgb: '7FB1ED' } }, alignment: { horizontal: 'center' } }
  };

  if (sideTableHeader.length > 0) {
    table_name_row[indicatorKeys.length + 1] = {
      v: data_set,
      t: 's',
      s: { fill: { fgColor: { rgb: '9CBCD3' } }, alignment: { horizontal: 'center' } }
    };
  }

  const table_name_row_index = xlsxLines.length;
  xlsxLines.push(table_name_row);
  xlsxLines.push(tableHeading.map(rotatedColumnHeaderCell));

  const mk5TableData = state.point_data_mcarthur_mk5.table_data;
  const leafletTableData = state.point_data_leaflet_80.table_data;
  const lagHours = lagHoursForDataSet(state, data_set);
  const weatherTable = applyLagTempRhShift(state.point_data_weather.table_data, mods, lagHours);
  const displayMods = displayModificationsForLag(state.point_data_weather.table_data, mods, lagHours);
  const weather = _.sortBy(_.values(weatherTable), 'sortIndex');

  weather.filter(w => w.sortIndex >= start_time && w.sortIndex < end_time).forEach((w, index) => {
    const row = [];
    row.push({ v: moment(w.time).format(DATE_FORMAT), t: 's', s: getCellStyle(false, null, '', index) });
    const rowKey = w.name;

    indicatorKeys.forEach((k) => {
      const { val, modified, useTextType } = weatherExportCell(
        k, w, displayMods, rowKey, mk5TableData, leafletTableData
      );
      const cellType = useTextType ? 's' : 'n';
      row.push({ v: val, t: cellType, s: getCellStyle(modified, val, k, index) });
    });

    if (sideTableHeader.length > 0) {
      sideTableHeader.forEach((h) => {
        const stRow = sideTableData[rowKey] || {};
        const originalVal = stRow[h.key];
        const modifiedVal = stRow[h.key + '_modified'];
        const val = modifiedVal === undefined ? originalVal : modifiedVal;
        const formattedVal = data_set === DATA_SETS.PRESCRIPTIONS ? toPrescriptionIcon(val) : val;
        const type = data_set === DATA_SETS.PRESCRIPTIONS ? 's' : 'n';
        if (h.colors) {
          const fgColor = getScaleColor(val, h.colors);
          row.push({
            v: formattedVal,
            t: type,
            s: getCellStyle(modifiedVal !== undefined, val, h.key, index, fgColor)
          });
        } else {
          row.push({ v: formattedVal, t: type, s: getCellStyle(modifiedVal !== undefined, val, h.key, index) });
        }
      });
    }
    xlsxLines.push(row);
  });

  const merge = [
    {
      s: { r: table_name_row_index, c: 1 },
      e: { r: table_name_row_index, c: indicatorKeys.length - exportConfig.behaviourSummaryWidth }
    },
    {
      s: { r: table_name_row_index, c: indicatorKeys.length - exportConfig.behaviourSummaryWidth + 1 },
      e: { r: table_name_row_index, c: indicatorKeys.length }
    }
  ];
  if (sideTableHeader.length > 0) {
    merge.push({
      s: { r: table_name_row_index, c: 1 + indicatorKeys.length },
      e: { r: table_name_row_index, c: indicatorKeys.length + sideTableHeader.length }
    });
  }

  // Create worksheet with rows; Add worksheet to workbook
  const ws = XLSX.utils.aoa_to_sheet(xlsxLines);
  ws['!merges'] = merge;

  // Adjust the cell width with the content
  ws['!cols'] =
    setWidths([18, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8]);

  // sheet name cannot exceed 31 chars
  XLSX.utils.book_append_sheet(wb, ws, format.replace(' + ', '&'));

  return wb;
}

function setWidths(widths) {
  return widths.map(i => {return { wch: i };});
}

function _getData(format, state, modifications, start_time, end_time) {
  if (format === EXPORT_FORMATS.PHOENIX) {
    return _hpPhoenixData(state, modifications, start_time, end_time);
  }
  if (format === EXPORT_FORMATS.ISP) {
    return _hpIspData(state, modifications, start_time, end_time);
  }
  if (XLSX_EXPORT_FORMATS.has(format)) {
    return _hpWeatherData(state, modifications, format, start_time, end_time);
  }
  return null;
}

function _getFilename(format) {
  const d = moment().format('YYYY-MM-DD-HH-mm');
  const builder = FILENAME_BY_FORMAT[format];
  return builder ? builder(d) : `ForesightExport_${d}.csv`;
}

export const exportData = store => next => action => {
  switch (action.type) {
    case ActionTypes.EXPORT_DATA:
      const state = store.getState();
      const modifications = action.include_manual && state.manual_weather.active ?
        state.manual_weather.active.properties.data :
        null;

      const { starting_day, days_lasting } = action;

      const now = new Date();
      const start_time = new Date(addDays(lastMidnightDate(now), starting_day - 1)).getTime();
      const end_time = new Date(addDays(lastMidnightDate(now), starting_day + days_lasting - 1)).getTime();
      if (action.format === EXPORT_FORMATS.PHOENIX) {
        const blob = new Blob([_getData(action.format, state, modifications, start_time, end_time)],
          { type: 'text/plain;charset=utf-8' });
        if (blob !== null) {
          FileSaver.saveAs(blob, _getFilename(action.format));
        }
      } else {
        const wb = _getData(action.format, state, modifications, start_time, end_time);
        if (wb) {
          XLSX.writeFile(wb, _getFilename(action.format));
        }
      }
      break;
    default:
      break;
  }
  return next(action);
};



// WEBPACK FOOTER //
// ./src/middleware/exportMiddleware.js